import React, { useEffect, useState } from 'react';

import { Checkbox, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import { ReactComponent as Chevron } from '../../../../../assets/icons/chevron-down-black.svg';
import EmptyCollaborators from '../../../../../shared/components/EmptyCollaborators/EmptyCollaborators';
import StyledButton from '../../../../../shared/components/StyledButton/StyledButton';
import StyledMenu, { IMenuItem } from '../../../../../shared/components/StyledMenu/StyledMenu';
import TablePagination from '../../../../../shared/components/TablePagination/TablePagination';
import { LOADING_TABLE_ROWS } from '../../../../../shared/constants/constants';
import { DEFAULT_PAGINATOR_STATE } from '../../../../../shared/constants/settings';
import {
  CLICK_ACTION,
  FILTER_ALL,
  FILTER_CURRENT_PAGE,
  SAVE_PEOPLE_RECEIVE_POINTS,
  SECTION_COLLABORATORS,
  SELECT_COLLABORATORS_POINTS_FILTER
} from '../../../../../shared/constants/tagManager';
import { useIsMounted } from '../../../../../shared/hooks/useIsMounted';
import { IMembersData } from '../../../../../shared/interfaces/businessData';
import { ISelectedMap, ITableState } from '../../../../../shared/interfaces/tables';
import tagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATION_POINTS } from '../../../../../shared/translations/rewards';
import { GenericVoidFunction } from '../../../../../shared/types/genericTypes';
import RewardsCollaboratorRow from '../RewardsCollaboratorRow/RewardsCollaboratorRow';

import './SendPointsTable.scss';

interface SendPointsTableProps {
  collaboratorsList: IMembersData[] | undefined;
  onSendPoints: GenericVoidFunction;
  error: boolean;
  emptySearch: boolean;
  filtersApplied: boolean;
  tableState?: ITableState;
  allCollaborators?: IMembersData[];
}

const SendPointsTable = ({
  collaboratorsList,
  onSendPoints,
  error,
  emptySearch,
  filtersApplied,
  tableState,
  allCollaborators
}: SendPointsTableProps) => {
  const [collaborators, setCollaborators] = useState<IMembersData[] | undefined>(undefined);
  const [paginatorState, setPaginatorState] = useState<number>(DEFAULT_PAGINATOR_STATE);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [currentPageData, setCurrentPageData] = useState<IMembersData[] | undefined>(undefined);
  const [selectedMap, setSelectedMap] = useState<ISelectedMap>({});
  const [isAscending, setIsAscending] = useState<boolean>(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation();
  const isMounted = useIsMounted();

  const handleSelectAll = (event: any) => {
    if (areAllSelected()) {
      onSelectAll(false);
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const getTotalCollaborators = () => {
    return collaboratorsList ? collaboratorsList.length : 0;
  };

  const paginateCollaborators = () => {
    const from = currentPage * paginatorState;
    const to = from + paginatorState;
    const pageData = collaborators?.slice(from, to);
    setCurrentPageData(pageData);
  };

  const areAllSelected = () => {
    if (!currentPageData) return false;
    return currentPageData.length <= Object.values(selectedMap).filter((value) => value).length;
  };

  const selectedNum = () => {
    return Object.values(selectedMap).filter((value) => value).length;
  };

  const onSelectAll = (all: boolean) => {
    if (!collaborators || !currentPageData) return;
    const newMap = (all ? collaborators : currentPageData).reduce((map: ISelectedMap, obj) => {
      map[obj.email] = !areAllSelected();
      return map;
    }, {});
    setSelectedMap(newMap);
    handleMenuClose();
  };

  const onCheckRow = (key: string) => {
    setSelectedMap({ ...selectedMap, [key]: !selectedMap[key] });
  };

  const sort = () => {
    if (!collaborators) return;
    setCollaborators([...collaborators].reverse());
    setIsAscending(!isAscending);
  };

  const onSaveSelection = () => {
    if (!allCollaborators) return;
    const selectedCollaborators = allCollaborators.filter((collaborator) => selectedMap[collaborator.email]);
    tagManagerService.pushEvent(CLICK_ACTION, SAVE_PEOPLE_RECEIVE_POINTS);
    onSendPoints(selectedCollaborators, { selectedMap, paginatorState, currentPage, isAscending });
  };

  const setInitialTableState = () => {
    if (tableState) {
      setSelectedMap(tableState.selectedMap);
      setCurrentPage(tableState.currentPage);
      setPaginatorState(tableState.paginatorState);
      setIsAscending(tableState.isAscending);
      setCollaborators(
        collaboratorsList?.sort((a, b) =>
          tableState.isAscending ? a.email.localeCompare(b.email) : b.email.localeCompare(a.email)
        )
      );
    }
  };

  const onPageChange = (index: number) => {
    setCurrentPage(index);
  };

  const onRowsPerPageChange = (value: number) => {
    setPaginatorState(value);
    setCurrentPage(0);
  };

  const selectAll = () => {
    onSelectAll(true);
    tagManagerService.pushEvent(CLICK_ACTION, SELECT_COLLABORATORS_POINTS_FILTER, FILTER_ALL);
  };

  const selectPage = () => {
    onSelectAll(false);
    tagManagerService.pushEvent(CLICK_ACTION, SELECT_COLLABORATORS_POINTS_FILTER, FILTER_CURRENT_PAGE);
  };

  useEffect(() => {
    const totalColaborators = getTotalCollaborators();
    if (totalColaborators) {
      paginateCollaborators();
    }
  }, [collaborators]);

  useEffect(() => {
    paginateCollaborators();
  }, [currentPage, paginatorState]);

  useEffect(() => {
    if (isMounted) {
      setCollaborators(collaboratorsList?.sort((a, b) => a.email.localeCompare(b.email)));
    }
  }, [collaboratorsList, isMounted]);

  useEffect(() => {
    setInitialTableState();
  }, [tableState]);

  useEffect(() => {
    setCurrentPage(0);
  }, [collaborators, paginatorState]);

  const optionMenu: IMenuItem[] = [
    { label: t(TRANSLATION_POINTS.send_points.table.select_all_menu.all), action: selectAll },
    { label: t(TRANSLATION_POINTS.send_points.table.select_all_menu.page), action: selectPage }
  ];

  const toolbar = (
    <div className="selection-actions">
      <div className="collaborators-count">
        {t(
          selectedNum() > 1
            ? TRANSLATION_POINTS.send_points.table.toolbar.employeesSelectedPlural
            : TRANSLATION_POINTS.send_points.table.toolbar.employeesSelected,
          { number: selectedNum() }
        )}
      </div>
      <StyledButton buttonType="secondary" classes="w-auto" action={onSaveSelection}>
        {t(TRANSLATION_POINTS.send_points.table.toolbar.actionButton)}
      </StyledButton>
    </div>
  );

  const skeletons = (
    <>
      {[...Array(LOADING_TABLE_ROWS).keys()].map(() => (
        <tr key={uuid()}>
          <td colSpan={5}>
            <Skeleton animation="wave" variant="text" width={'100%'} height={60} />
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <div className="send-points-table-container">
      {emptySearch || error ? (
        <EmptyCollaborators filtersApplied={filtersApplied} />
      ) : (
        <>
          <table className="send-points-table">
            <thead>
              <tr className="table-header">
                <th className="table-column-cell w-1">
                  <div className="flex flex-row items-center">
                    <Checkbox className="checkbox" checked={areAllSelected()} onChange={handleSelectAll} />
                    <Chevron className="checkbox-chevron" />
                  </div>
                  <StyledMenu
                    anchorEl={anchorEl}
                    handleClose={handleMenuClose}
                    menuItems={optionMenu}
                    anchorOrigin={undefined}
                    transformOrigin={undefined}
                  />
                </th>
                <th className="table-column-cell sortable" onClick={sort}>
                  {t(TRANSLATION_POINTS.send_points.table.header.email)}
                  <Chevron className={`sort-chevron ${isAscending ? 'asc' : 'desc'}`} />
                </th>
                <th className="table-column-cell table-hide-tablet">
                  {t(TRANSLATION_POINTS.send_points.table.header.id)}
                </th>
                <th className="table-column-cell table-hide-tablet">
                  {t(TRANSLATION_POINTS.send_points.table.header.name)}
                </th>
                <th className="table-column-cell table-hide-tablet w-1">
                  {t(TRANSLATION_POINTS.send_points.table.header.status)}
                </th>
              </tr>
              {selectedNum() > 0 && (
                <tr className="toolbar">
                  <th colSpan={4}>{toolbar}</th>
                </tr>
              )}
            </thead>
            <tbody className="table-body">
              {!collaborators?.length
                ? skeletons
                : currentPageData?.map((collaborator) => (
                    <RewardsCollaboratorRow
                      collaborator={collaborator}
                      key={uuid()}
                      onCheck={onCheckRow}
                      checked={!!selectedMap[collaborator.email]}
                    />
                  ))}
            </tbody>
          </table>
          <TablePagination
            rowsPerPageOptions={[5, 15, 25, 50]}
            rowsPerPage={paginatorState}
            count={getTotalCollaborators()}
            actualPage={currentPage}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            sectionPage={SECTION_COLLABORATORS}
            marginTop={20}
          />
        </>
      )}
    </div>
  );
};

export default SendPointsTable;
